import { useEffect, useState } from "react";

import { Card } from "@/components/ui/card";

import { useUpdateScreeningPromptCandidacy } from "@/hooks/mutations/useUpdateScreeningPromptCandidacy";
import { useUpdateScreeningPromptInterview } from "@/hooks/mutations/useUpdateScreeningPromptInterview";
import { CORNER_POPUP_HEIGHT_CLASS } from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  ActiveScreeningPrompt,
  CandidacyStatus,
  InterviewStatus,
} from "@/types";

import { RejectReasonStep } from "./RejectReasonStep";
import { RejectScreenedStep } from "./RejectScreenedStep";
import { RescheduleStep } from "./RescheduleStep";
import { ScreeningHomeContent } from "./ScreeningHomeContent";

enum ScreeningPromptStep {
  HOME = "HOME",
  REJECTED_WERE_THEY_SCREENED = "REJECTED_WERE_THEY_SCREENED",
  REJECTED_REASON = "REJECTED_REASON",
  RESCHEDULE_SELECTION = "RESCHEDULE_SELECTION",
}

// The card for one active screen. CornerPromptStack picks which screen surfaces
// and owns dismissal, so this only runs the prompt's own flow.
export const ScreeningInProgressContainer = ({
  activeScreen,
  onDismiss,
  onDismissAll,
}: {
  activeScreen: ActiveScreeningPrompt;
  onDismiss: () => void;
  onDismissAll?: () => void;
}) => {
  /* Database CRUD Operations */
  const { mutate: updateCandidacy, isPending: isCandidacyUpdatePending } =
    useUpdateScreeningPromptCandidacy();
  const { mutate: updateInterview, isPending: isInterviewUpdatePending } =
    useUpdateScreeningPromptInterview();

  /* React Hooks */
  const [step, setStep] = useState<ScreeningPromptStep>(
    ScreeningPromptStep.HOME,
  );

  /* Helpers */
  const isUpdatePending = isCandidacyUpdatePending || isInterviewUpdatePending;

  // Reset to HOME whenever the surfaced screen changes so the next screen never
  // opens mid-flow.
  useEffect(() => {
    setStep(ScreeningPromptStep.HOME);
  }, [activeScreen.interviewId]);

  /* Render */
  const render = () => {
    return (
      <Card
        data-screening-card // Add global identifier, so we can make exceptions for closing modals like candidate sheet
        className={cn(
          "pointer-events-auto w-full overflow-hidden rounded-xl border-slate-200 bg-white p-0 shadow-lg",
          // The flow steps carry a header plus their own controls, so only the
          // home step can hold the notification popup's exact height.
          step === ScreeningPromptStep.HOME && CORNER_POPUP_HEIGHT_CLASS,
        )}
      >
        <ScreeningInProgressContent
          activeScreen={activeScreen}
          isUpdatePending={isUpdatePending}
          onDismiss={onDismiss}
          onDismissAll={onDismissAll}
          setStep={setStep}
          step={step}
          updateCandidacy={updateCandidacy}
          updateInterview={updateInterview}
        />
      </Card>
    );
  };

  return render();
};

const ScreeningInProgressContent = ({
  activeScreen,
  isUpdatePending,
  onDismiss,
  onDismissAll,
  setStep,
  step,
  updateCandidacy,
  updateInterview,
}: {
  activeScreen: ActiveScreeningPrompt;
  isUpdatePending: boolean;
  onDismiss: () => void;
  onDismissAll?: () => void;
  setStep: (step: ScreeningPromptStep) => void;
  step: ScreeningPromptStep;
  updateCandidacy: ReturnType<
    typeof useUpdateScreeningPromptCandidacy
  >["mutate"];
  updateInterview: ReturnType<
    typeof useUpdateScreeningPromptInterview
  >["mutate"];
}) => {
  const { companyId, jobId, candidacyId, interviewId } = activeScreen;
  const name = formatting.fullName(
    activeScreen.firstName,
    activeScreen.lastName,
    "Candidate",
  );

  const goHome = () => {
    setStep(ScreeningPromptStep.HOME);
  };

  const openProfile = () => {
    window.open(
      `/companies/${companyId}/jobs/${jobId}?c=${candidacyId}`,
      "_blank",
    );
  };

  // No Show: mark the screen as a no-show, move the candidacy back to Engaged.
  const handleNoShow = () => {
    mixpanelUtils.trackButtonClick(
      "No Show",
      MIXPANEL_SCREENS.SCREENING_IN_PROGRESS,
      {
        candidacy_id: candidacyId,
        interview_id: interviewId,
      },
    );
    // Candidacy write goes after the interview update settles — the BE moves
    // the candidacy as a side effect of the interview status change, and firing
    // both concurrently lets that side effect overwrite the status we set here.
    updateInterview(
      {
        companyId,
        jobId,
        interviewId,
        updateInterviewRequest: { status: InterviewStatus.CANDIDATE_NO_SHOW },
      },
      {
        onSuccess: () => {
          updateCandidacy({
            companyId,
            jobId,
            candidacyId,
            updateCandidacyRequest: { status: CandidacyStatus.ENGAGED },
          });
        },
      },
    );
  };

  // Move To Review: complete the screen; viaScreened counts it on the leaderboard.
  const handleMoveToReview = () => {
    mixpanelUtils.trackButtonClick(
      "Move To Review",
      MIXPANEL_SCREENS.SCREENING_IN_PROGRESS,
      {
        candidacy_id: candidacyId,
        interview_id: interviewId,
      },
    );
    updateInterview(
      {
        companyId,
        jobId,
        interviewId,
        updateInterviewRequest: { status: InterviewStatus.COMPLETED },
      },
      {
        onSuccess: () => {
          updateCandidacy({
            companyId,
            jobId,
            candidacyId,
            updateCandidacyRequest: {
              status: CandidacyStatus.TOREVIEW,
              viaScreened: true,
            },
          });
        },
      },
    );
  };

  // Push screen back to a new time
  const handleReschedule = (isoDateTime: string) => {
    mixpanelUtils.trackButtonClick(
      "Reschedule",
      MIXPANEL_SCREENS.SCREENING_IN_PROGRESS,
      {
        candidacy_id: candidacyId,
        interview_id: interviewId,
      },
    );
    updateInterview({
      companyId,
      jobId,
      interviewId,
      updateInterviewRequest: { dateTime: isoDateTime },
    });
  };

  // Reject candidacy and update internal interview accordingly
  const handleReject = (screened: boolean, justification: string | null) => {
    mixpanelUtils.trackButtonClick(
      "Reject",
      MIXPANEL_SCREENS.SCREENING_IN_PROGRESS,
      {
        candidacy_id: candidacyId,
        interview_id: interviewId,
        screened,
      },
    );
    updateInterview(
      {
        companyId,
        jobId,
        interviewId,
        updateInterviewRequest: {
          status: screened
            ? InterviewStatus.COMPLETED
            : InterviewStatus.CANDIDATE_NO_SHOW,
        },
      },
      {
        onSuccess: () => {
          updateCandidacy({
            companyId,
            jobId,
            candidacyId,
            updateCandidacyRequest: {
              status: CandidacyStatus.REJECTED,
              viaScreened: screened,
              statusChangeReason: justification,
            },
          });
        },
      },
    );
  };

  const render = () => {
    if (step === ScreeningPromptStep.RESCHEDULE_SELECTION) {
      return (
        <RescheduleStep
          dateTime={activeScreen.dateTime}
          isUpdatePending={isUpdatePending}
          onBack={goHome}
          onDismiss={onDismiss}
          onReschedule={handleReschedule}
        />
      );
    }

    if (step === ScreeningPromptStep.REJECTED_WERE_THEY_SCREENED) {
      return (
        <RejectScreenedStep
          candidateName={name}
          isUpdatePending={isUpdatePending}
          onBack={goHome}
          onDismiss={onDismiss}
          onNo={() => {
            handleReject(false, null);
          }}
          onYes={() => {
            setStep(ScreeningPromptStep.REJECTED_REASON);
          }}
        />
      );
    }

    if (step === ScreeningPromptStep.REJECTED_REASON) {
      return (
        <RejectReasonStep
          isUpdatePending={isUpdatePending}
          onBack={() => {
            setStep(ScreeningPromptStep.REJECTED_WERE_THEY_SCREENED);
          }}
          onDismiss={onDismiss}
          onConfirm={(justification) => {
            handleReject(true, justification);
          }}
        />
      );
    }

    return (
      <ScreeningHomeContent
        activeScreen={activeScreen}
        isUpdatePending={isUpdatePending}
        onDismiss={onDismiss}
        onDismissAll={onDismissAll}
        onMoveToReview={handleMoveToReview}
        onNoShow={handleNoShow}
        onOpenProfile={openProfile}
        onStartReject={() => {
          setStep(ScreeningPromptStep.REJECTED_WERE_THEY_SCREENED);
        }}
        onStartReschedule={() => {
          setStep(ScreeningPromptStep.RESCHEDULE_SELECTION);
        }}
      />
    );
  };

  return render();
};
